/**
 * registerServiceWorker — installs public/sw.js, safely.
 *
 * =====================================================================
 * WHY THIS IS PRODUCTION-ONLY
 * =====================================================================
 * CRA's dev server (`npm start`) serves an unhashed bundle over a
 * webpack-dev-server that pushes updates via websocket. A service worker
 * in front of that fights hot-reload: the SW answers from cache, the
 * dev server's in-memory bundle moves on, and you end up editing a file
 * and seeing nothing change — or worse, seeing a bundle from twenty
 * edits ago. The standard symptom is "my changes don't appear until I
 * hard-refresh", and it wastes an afternoon before anyone suspects the
 * worker.
 *
 * Worse, a worker registered once in dev stays registered on
 * localhost:3000 FOREVER, including across `npm start` restarts and
 * across branches. So this module does two things:
 *
 *   register()   — no-ops unless NODE_ENV === 'production'.
 *   unregister() — actively tears down any worker already registered on
 *                  this origin, and is called automatically in dev. That
 *                  cleans up a machine where someone experimented, and
 *                  it is the escape hatch if a bad worker ever ships.
 *
 * The dev server and the production build share http://localhost origin
 * on this machine, so without that automatic unregister a single
 * production preview would poison the dev environment.
 */

const SW_URL = '/sw.js';

/** True on localhost — used to keep the "is it really https?" check honest. */
const isLocalhost = typeof window !== 'undefined' && Boolean(
  window.location.hostname === 'localhost'
  || window.location.hostname === '[::1]'
  || window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d?\d)){3}$/)
);

function supported() {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    // Service workers require a secure context. They are allowed on
    // localhost, which is what makes a local production build testable.
    && (window.isSecureContext || isLocalhost);
}

/**
 * Register the worker.
 *
 * @param {object} [opts]
 * @param {Function} [opts.onUpdate]  called with the waiting registration
 *        when a NEW version has been downloaded and is ready. Wire this
 *        to a toast: "يتوفر إصدار جديد — أعد تحميل الصفحة".
 * @param {Function} [opts.onReady]   called once the worker controls the
 *        page and offline mode is genuinely available.
 * @param {boolean} [opts.force=false] register even in development.
 *        For deliberately testing the worker; never commit a call with
 *        this set.
 */
export function registerServiceWorker({ onUpdate, onReady, force = false } = {}) {
  if (!supported()) return;

  // THE DEV GUARD. See the header.
  if (process.env.NODE_ENV !== 'production' && !force) {
    unregisterServiceWorker();
    return;
  }

  // Register after `load` so downloading and parsing the worker never
  // competes with the first paint on a slow connection.
  window.addEventListener('load', () => {
    navigator.serviceWorker.register(SW_URL)
      .then((registration) => {
        // A worker is already waiting: the user has an old tab open and
        // a new version was deployed while they were using it.
        if (registration.waiting && navigator.serviceWorker.controller) {
          if (onUpdate) onUpdate(registration);
        }

        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;

          installing.addEventListener('statechange', () => {
            if (installing.state !== 'installed') return;

            if (navigator.serviceWorker.controller) {
              // An OLD worker is in control, so this is an update, not a
              // first install. The new one is waiting.
              if (onUpdate) onUpdate(registration);
            } else if (onReady) {
              // First install. Content is now cached for offline use.
              onReady(registration);
            }
          });
        });

        // Ask the browser to re-check sw.js when the tab regains focus.
        // On a machine that never closes the tab this is what makes a
        // deploy reach the user the same day.
        if (typeof document !== 'undefined') {
          document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
              registration.update().catch(() => { /* offline; try later */ });
            }
          });
        }
      })
      .catch(() => {
        // A failed registration must be completely invisible: the app
        // works fine without a worker, it is just not offline-capable.
      });
  });

  // When the new worker takes control, reload once so the page is
  // running the code that matches the assets now being served. The
  // `refreshing` latch prevents the infamous reload loop.
  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  });
}

/**
 * Tell the waiting worker to activate now. Call this from the "reload to
 * update" button in your onUpdate toast; `controllerchange` above then
 * reloads the page.
 */
export function applyUpdate(registration) {
  const waiting = registration && registration.waiting;
  if (waiting) waiting.postMessage({ type: 'SKIP_WAITING' });
}

/**
 * Remove any worker registered on this origin and drop its caches.
 * Called automatically in development, and worth calling on logout on a
 * shared machine.
 */
export function unregisterServiceWorker() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  navigator.serviceWorker.getRegistrations()
    .then((regs) => regs.forEach((r) => r.unregister()))
    .catch(() => { /* nothing to do */ });

  if (typeof caches !== 'undefined') {
    caches.keys()
      .then((names) => names.filter((n) => n.startsWith('mtz-static-')).forEach((n) => caches.delete(n)))
      .catch(() => { /* nothing to do */ });
  }
}

/** Ask the active worker to drop its static caches (logout hygiene). */
export function clearStaticCaches() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  const sw = navigator.serviceWorker.controller;
  if (sw) sw.postMessage({ type: 'CLEAR_CACHES' });
}

export default registerServiceWorker;
